import { Avatar, Text, tokens, Divider } from '@hotjar/hotsauce';
import { useMemo } from 'react';
import { useCurrentOrganization } from '@hotjar/webapp/hooks/useCurrentOrganization';
import styled from '@emotion/styled';
import { epochDate } from '@hotjar/webapp/lib/time';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { CreatedByUser } from '../../domain/common/CreatedByUser';

const StyledAvatar = styled(Avatar)`
  flex-shrink: 0;
`;

const TruncatedText = styled(Text)`
  ${tokens.truncateText()}
  max-width: 120px;
`;

const StyledDivider = styled(Divider)`
  height: ${tokens.spacer.x3};
`;

export function EntityCreator({
  createdBy,
  createdTime,
}: {
  createdBy: CreatedByUser;
  createdTime: number;
}): JSX.Element {
  const { t } = useTranslationWithPrefix('feedback:entity-creator');
  const { data: organization } = useCurrentOrganization();
  type OrganizationMember = NonNullable<typeof organization>['users'][number];
  const organizationUsers = useMemo(
    () =>
      organization?.users.reduce<Partial<Record<number, OrganizationMember>>>((users, user) => {
        return { ...users, [user.id]: user };
      }, {}),
    [organization],
  );

  const userAvatarUrl = useMemo(() => {
    return createdBy.id !== null &&
      organizationUsers !== undefined &&
      organizationUsers[createdBy.id]?.avatar !== null
      ? organizationUsers[createdBy.id]?.avatar
      : null;
  }, [organizationUsers, createdBy]);

  const userName = useMemo(() => {
    if (createdBy.status === 'deleted') return t('user-deleted');
    return createdBy.name ?? t('user-unknown');
  }, [createdBy.status, createdBy.name, t]);

  return (
    <>
      {userAvatarUrl !== null && (
        <StyledAvatar src={userAvatarUrl} userName={userName} hideTooltip size="s" />
      )}
      <TruncatedText textType="caption" color="text_light">
        {userName}
      </TruncatedText>
      <Text textType="caption" color="text_light">
        {' · '}
      </Text>
      <Text textType="caption" color="text_light">
        {epochDate(createdTime, 'D MMM YYYY')}
      </Text>
      <StyledDivider orientation="vertical" />
    </>
  );
}
